import {
    filter,
    ifElse,
    invertObj,
    isNil,
    map,
    pipe,
    reduce,
    sortBy,
    pick,
    toPairs,
    keys,
    mapObjIndexed,
    pickBy
} from 'ramda';
import { LIMIT_FIELD_NAMES, LIMIT_TYPES, LIMIT_DEFAULTS_FIELD_NAMES, LIMIT_FIELD_EXTRA_DATA } from './constants';
import { parseImsDate } from '@core/js/hooks/useFormatter/helpers';

const { BAN_ON_BET_AFTER_LOGOUT } = LIMIT_TYPES;
const parseLimitDate = pipe(
    ifElse(
        isNil,
        () => new Date,
        parseImsDate
    ),
    Number
);

export const parseDefaultLimits = pipe(
    pick(keys(LIMIT_DEFAULTS_FIELD_NAMES)),
    toPairs,
    map(([limitType, limit]) => ({
        limitType: LIMIT_DEFAULTS_FIELD_NAMES[limitType],
        limit
    }))
);

export const parseConfig = (config) => {
    const {
        betLimitConfigurationItems,
        lossLimitConfigurationItems,
        loginLimitsConfigurationItems
    } = config;

    return {
        betLimits: betLimitConfigurationItems[0],
        lossLimits: lossLimitConfigurationItems[0],
        loginLimits: loginLimitsConfigurationItems[0]
    };
};

export const parseLimitOptions = config => {
    const parsedConfig = parseConfig(config);
    const loginLimitsRaw = parseDefaultLimits(parsedConfig.loginLimits);

    const { limits, pendingLimits } = parseLimits(loginLimitsRaw, config.loginLimitsConfigurationItems[0]);
    const initialValues = getInitialValues(limits);
    return {
        config: config.loginLimitsConfigurationItems[0],
        limits,
        pendingLimits,
        initialValues
    };
};

export const parseLimits = (rawLimits = [], config = []) =>
    pipe(
        filter(i => i.limitType !== BAN_ON_BET_AFTER_LOGOUT && config[LIMIT_FIELD_NAMES[i.limitType]]),
        sortBy(i => i.limitType),
        reduce(
            ({ limits, pendingLimits }, { startDate, endDate, ...rest }) => {
                const limitsMap = parseLimitDate(startDate) > Date.now()
                    ? pendingLimits
                    : limits;

                limitsMap.push({
                    ...rest,
                    startDate: parseLimitDate(startDate),
                    endDate: parseLimitDate(endDate)
                });

                return { limits, pendingLimits };
            },
            { limits: [], pendingLimits: [] }
        )
    )(rawLimits);

export const getInitialValues = (limits = []) => ({
    ...map((limitType) => {
        const item = limits.find(i => i.limitType === Number(limitType));
        return item ? item.limit : 0;
    })(invertObj(LIMIT_FIELD_NAMES)),
    fetchLimitsTimestamp: Date.now()
});

export const convertTime = minutes => ({
    dd: Math.floor(minutes / 24 / 60),
    hh: Math.floor(minutes / 60 % 24),
    mm: minutes % 60
});

export const reverseConvertTime = ({ dd, hh, mm }) => dd * 24 * 60 + hh * 60 + mm;

export const parseLimitsForHiddenForm = (limits = [], config) => pipe(
    invertObj,
    mapObjIndexed((type, name) => {
        const limitType = Number(type);
        const item = limits.find(i => i.limitType === limitType);

        return {
            ...item || { limitType },
            ...LIMIT_FIELD_EXTRA_DATA[limitType],
            name,
            allowed: config[name]
        };
    })
)(LIMIT_FIELD_NAMES);

const prepareDataForSave = (next, current) => pipe(
    pickBy((value, key) => current[key] !== Number(value)),
    map(Number)
)(next);

export const formatData = (data, form) => {
    const { initialValues } = form.getState();
    return prepareDataForSave(data, initialValues);
};
